// frontend/src/hooks/useSphereTouchControls.js
import { useRef, useEffect } from 'react';

export const useSphereTouchControls = (
  containerRef,
  optionsLength,
  disabled,
  selectedIndex,
  setActiveIndex,
  onDoubleTap
) => {
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isSwiping = useRef(false);
  const lastTap = useRef({ time: 0, x: 0, y: 0 });

  useEffect(() => {
    if (disabled || !optionsLength) return;

    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e) => {
      if (disabled || !e.touches[0]) return;
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
      isSwiping.current = false;
    };

    const handleTouchMove = (e) => {
      if (selectedIndex !== null || disabled || !e.touches[0]) return;

      const deltaX = e.touches[0].clientX - touchStartX.current;
      const deltaY = e.touches[0].clientY - touchStartY.current;

      if (Math.abs(deltaX) > Math.abs(deltaY)) { // Horizontal swipe is dominant
        isSwiping.current = true;
        e.preventDefault(); // Prevent scroll only on horizontal swipe

        if (deltaX > 50) { // Swipe Right
          setActiveIndex(prev => (prev > 0 ? prev - 1 : optionsLength - 1));
          touchStartX.current = e.touches[0].clientX;
        } else if (deltaX < -50) { // Swipe Left
          setActiveIndex(prev => (prev < optionsLength - 1 ? prev + 1 : 0));
          touchStartX.current = e.touches[0].clientX;
        }
      } else {
        isSwiping.current = false;
      }
    };

    const handleTouchEnd = (e) => {
      if (disabled) return;

      const touch = e.changedTouches && e.changedTouches[0];
      if (touch && !isSwiping.current && typeof onDoubleTap === 'function') {
        const deltaX = touch.clientX - touchStartX.current;
        const deltaY = touch.clientY - touchStartY.current;
        const isTap = Math.abs(deltaX) < 12 && Math.abs(deltaY) < 12;
        const target = e.target;
        const isUiControl = target?.closest?.(
          '.sphere-menu-dots, .emotion-sphere-dots, [data-sphere-touch-ignore], button, a, input, textarea, select'
        );

        if (isTap && !isUiControl) {
          const now = Date.now();
          const previous = lastTap.current;
          const distance = Math.hypot(touch.clientX - previous.x, touch.clientY - previous.y);

          if (now - previous.time < 320 && distance < 40) {
            e.preventDefault();
            lastTap.current = { time: 0, x: 0, y: 0 };
            onDoubleTap(e);
          } else {
            lastTap.current = { time: now, x: touch.clientX, y: touch.clientY };
          }
        }
      }

      isSwiping.current = false;
    };

    container.addEventListener("touchstart", handleTouchStart, { passive: true });
    container.addEventListener("touchmove", handleTouchMove, { passive: false });
    container.addEventListener("touchend", handleTouchEnd, { passive: false });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [containerRef, optionsLength, disabled, selectedIndex, setActiveIndex, onDoubleTap]);
};
